export interface BrandLogoItem {
  id: string;
  brandName: string;
  name: string; // Alias for backward compatibility
  category: 'CRYPTO EXCHANGES' | 'CRYPTO COINS' | 'PAYMENT PROCESSORS' | 'MOBILE BANKING' | 'DIGITAL PAYMENTS' | 'BANKING' | 'CUSTOM';
  officialSVG: string;
  logoUrl: string; // Alias for backward compatibility
  brandColor?: string;
  searchKeywords: string[];
  keywords?: string[]; // Alias for backward compatibility
  isOfficialVector: boolean;
}

function makeSvgDataUri(svgContent: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgContent.trim())}`;
}

export const BRAND_LOGO_CATEGORIES = [
  'ALL',
  'CRYPTO EXCHANGES',
  'CRYPTO COINS',
  'PAYMENT PROCESSORS',
  'MOBILE BANKING',
  'DIGITAL PAYMENTS',
  'BANKING',
  'CUSTOM',
] as const;

export type BrandCategory = typeof BRAND_LOGO_CATEGORIES[number];

const paypalSvg = 'https://cdn.worldvectorlogo.com/logos/paypal-3.svg';
const stripeSvg = 'https://cdn.worldvectorlogo.com/logos/stripe-4.svg';
const nowPaymentsSvg = 'https://avatars.githubusercontent.com/u/60699268?s=200&v=4';
const sslCommerzSvg = 'https://sslcommerz.com/wp-content/uploads/2021/11/logo.png';
const wiseSvg = 'https://cdn.worldvectorlogo.com/logos/wise-2.svg';
const skrillSvg = 'https://cdn.worldvectorlogo.com/logos/skrill-logo.svg';
const netellerSvg = 'https://cdn.worldvectorlogo.com/logos/neteller-1.svg';
const binanceSvg = 'https://cdn.worldvectorlogo.com/logos/binance.svg';
const coinbaseSvg = 'https://cdn.worldvectorlogo.com/logos/coinbase.svg';
const bybitSvg = 'https://cdn.worldvectorlogo.com/logos/bybit-1.svg';
const okxSvg = 'https://cdn.worldvectorlogo.com/logos/okx-1.svg';
const krakenSvg = 'https://cdn.worldvectorlogo.com/logos/kraken.svg';
const kucoinSvg = 'https://cryptologos.cc/logos/kucoin-token-kcs-logo.svg';
const usdtSvg = 'https://cdn.worldvectorlogo.com/logos/tether-1.svg';
const trc20Svg = 'https://cdn.worldvectorlogo.com/logos/tether-1.svg';
const btcSvg = 'https://cdn.worldvectorlogo.com/logos/bitcoin.svg';
const ethSvg = 'https://cdn.worldvectorlogo.com/logos/ethereum-1.svg';
const ltcSvg = 'https://cdn.worldvectorlogo.com/logos/litecoin.svg';
const usdcSvg = 'https://cryptologos.cc/logos/usd-coin-usdc-logo.svg';
const solanaSvg = 'https://cdn.worldvectorlogo.com/logos/solana.svg';
const cashappSvg = 'https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/cashapp.svg';
const venmoSvg = 'https://cdn.worldvectorlogo.com/logos/venmo.svg';
const zelleSvg = 'https://cdn.worldvectorlogo.com/logos/zelle-1.svg';
const applePaySvg = 'https://cdn.worldvectorlogo.com/logos/apple-pay-3.svg';
const googlePaySvg = 'https://cdn.worldvectorlogo.com/logos/google-pay-1.svg';
const chimeSvg = 'https://cdn.worldvectorlogo.com/logos/chime-1.svg';

const bankTransferSvg = makeSvgDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="100%" height="100%">
    <rect width="256" height="256" rx="48" fill="#0F172A"/>
    <path d="M128 48L48 96v16h160V96l-80-48zm-64 72v56h20v-56H64zm40 0v56h20v-56h-20zm40 0v56h20v-56h-20zm40 0v56h20v-56h-20zM48 192v16h160v-16H48z" fill="#38BDF8"/>
  </svg>
`);

const swiftSvg = makeSvgDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="100%" height="100%">
    <rect width="256" height="256" rx="48" fill="#0A2540"/>
    <path fill="#00D2FF" d="M48 112h32c8 0 14 4 14 10 0 8-8 10-14 10H64v16H48v-36zm16 12h14c3 0 5-1 5-3s-2-3-5-3H64v6zm40-12h16l10 24 10-24h16l-20 36h-12l-20-36zm56 0h16v36h-16v-36z"/>
  </svg>
`);

const achSvg = makeSvgDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="100%" height="100%">
    <rect width="256" height="256" rx="48" fill="#0052FF"/>
    <path fill="#FFFFFF" d="M64 148l24-56h20l24 56h-18l-5-12H86l-5 12H64zm26-24h20l-10-26-10 26zm60-24c12 0 20 8 20 20 0 12-8 20-20 20s-20-8-20-20c0-12 8-20 20-20zm0 14c-4 0-8 3-8 8s4 8 8 8 8-3 8-8-4-8-8-8z"/>
  </svg>
`);

export const BRAND_LOGOS: BrandLogoItem[] = [
  // --- PAYMENT PROCESSORS ---
  {
    id: 'paypal',
    brandName: 'PayPal',
    name: 'PayPal',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#003087',
    isOfficialVector: true,
    searchKeywords: ['paypal', 'express', 'checkout', 'wallet', 'credit card'],
    keywords: ['paypal', 'express', 'checkout', 'wallet', 'credit card'],
    officialSVG: paypalSvg,
    logoUrl: paypalSvg
  },
  {
    id: 'stripe',
    brandName: 'Stripe',
    name: 'Stripe',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#635BFF',
    isOfficialVector: true,
    searchKeywords: ['stripe', 'card', 'credit card', 'gateway', 'payment'],
    keywords: ['stripe', 'card', 'credit card', 'gateway', 'payment'],
    officialSVG: stripeSvg,
    logoUrl: stripeSvg
  },
  {
    id: 'nowpayments',
    brandName: 'NOWPayments',
    name: 'NOWPayments',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#22C55E',
    isOfficialVector: true,
    searchKeywords: ['nowpayments', 'crypto', 'gateway', 'multi-crypto', 'merchant'],
    keywords: ['nowpayments', 'crypto', 'gateway', 'multi-crypto', 'merchant'],
    officialSVG: nowPaymentsSvg,
    logoUrl: nowPaymentsSvg
  },
  {
    id: 'sslcommerz',
    brandName: 'SSLCommerz',
    name: 'SSLCommerz',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#203164',
    isOfficialVector: true,
    searchKeywords: ['sslcommerz', 'bd', 'local', 'gateway', 'card'],
    keywords: ['sslcommerz', 'bd', 'local', 'gateway', 'card'],
    officialSVG: sslCommerzSvg,
    logoUrl: sslCommerzSvg
  },
  {
    id: 'wise',
    brandName: 'Wise',
    name: 'Wise',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#9FE870',
    isOfficialVector: true,
    searchKeywords: ['wise', 'transferwise', 'international', 'bank'],
    keywords: ['wise', 'transferwise', 'international', 'bank'],
    officialSVG: wiseSvg,
    logoUrl: wiseSvg
  },
  {
    id: 'skrill',
    brandName: 'Skrill',
    name: 'Skrill',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#811231',
    isOfficialVector: true,
    searchKeywords: ['skrill', 'wallet', 'e-wallet'],
    keywords: ['skrill', 'wallet', 'e-wallet'],
    officialSVG: skrillSvg,
    logoUrl: skrillSvg
  },
  {
    id: 'neteller',
    brandName: 'Neteller',
    name: 'Neteller',
    category: 'PAYMENT PROCESSORS',
    brandColor: '#81B82A',
    isOfficialVector: true,
    searchKeywords: ['neteller', 'wallet', 'e-wallet'],
    keywords: ['neteller', 'wallet', 'e-wallet'],
    officialSVG: netellerSvg,
    logoUrl: netellerSvg
  },

  // --- CRYPTO EXCHANGES ---
  {
    id: 'binance',
    brandName: 'Binance',
    name: 'Binance',
    category: 'CRYPTO EXCHANGES',
    brandColor: '#F0B90B',
    isOfficialVector: true,
    searchKeywords: ['binance', 'crypto', 'exchange', 'cz', 'bnb'],
    keywords: ['binance', 'crypto', 'exchange', 'cz', 'bnb'],
    officialSVG: binanceSvg,
    logoUrl: binanceSvg
  },
  {
    id: 'coinbase',
    brandName: 'Coinbase',
    name: 'Coinbase',
    category: 'CRYPTO EXCHANGES',
    brandColor: '#0052FF',
    isOfficialVector: true,
    searchKeywords: ['coinbase', 'crypto', 'exchange', 'wallet'],
    keywords: ['coinbase', 'crypto', 'exchange', 'wallet'],
    officialSVG: coinbaseSvg,
    logoUrl: coinbaseSvg
  },
  {
    id: 'bybit',
    brandName: 'Bybit',
    name: 'Bybit',
    category: 'CRYPTO EXCHANGES',
    brandColor: '#F7A600',
    isOfficialVector: true,
    searchKeywords: ['bybit', 'exchange', 'crypto', 'derivatives'],
    keywords: ['bybit', 'exchange', 'crypto', 'derivatives'],
    officialSVG: bybitSvg,
    logoUrl: bybitSvg
  },
  {
    id: 'okx',
    brandName: 'OKX',
    name: 'OKX',
    category: 'CRYPTO EXCHANGES',
    brandColor: '#FFFFFF',
    isOfficialVector: true,
    searchKeywords: ['okx', 'okex', 'exchange', 'crypto'],
    keywords: ['okx', 'okex', 'exchange', 'crypto'],
    officialSVG: okxSvg,
    logoUrl: okxSvg
  },
  {
    id: 'kraken',
    brandName: 'Kraken',
    name: 'Kraken',
    category: 'CRYPTO EXCHANGES',
    brandColor: '#5741D9',
    isOfficialVector: true,
    searchKeywords: ['kraken', 'crypto', 'exchange'],
    keywords: ['kraken', 'crypto', 'exchange'],
    officialSVG: krakenSvg,
    logoUrl: krakenSvg
  },
  {
    id: 'kucoin',
    brandName: 'KuCoin',
    name: 'KuCoin',
    category: 'CRYPTO EXCHANGES',
    brandColor: '#24AE8F',
    isOfficialVector: true,
    searchKeywords: ['kucoin', 'crypto', 'exchange', 'kcs'],
    keywords: ['kucoin', 'crypto', 'exchange', 'kcs'],
    officialSVG: kucoinSvg,
    logoUrl: kucoinSvg
  },

  // --- CRYPTO COINS ---
  {
    id: 'trc20',
    brandName: 'USDT (TRC20)',
    name: 'USDT (TRC20)',
    category: 'CRYPTO COINS',
    brandColor: '#FF0033',
    isOfficialVector: true,
    searchKeywords: ['trc20', 'trc-20', 'usdt (trc20)', 'usdt trc20', 'tron', 'tether trc20', 'crypto'],
    keywords: ['trc20', 'trc-20', 'usdt (trc20)', 'usdt trc20', 'tron', 'tether trc20', 'crypto'],
    officialSVG: trc20Svg,
    logoUrl: trc20Svg
  },
  {
    id: 'usdt',
    brandName: 'USDT (Tether)',
    name: 'USDT (Tether)',
    category: 'CRYPTO COINS',
    brandColor: '#26A17B',
    isOfficialVector: true,
    searchKeywords: ['usdt', 'tether', 'stablecoin', 'trc20', 'erc20', 'crypto'],
    keywords: ['usdt', 'tether', 'stablecoin', 'trc20', 'erc20', 'crypto'],
    officialSVG: usdtSvg,
    logoUrl: usdtSvg
  },
  {
    id: 'bitcoin',
    brandName: 'BTC (Bitcoin)',
    name: 'BTC (Bitcoin)',
    category: 'CRYPTO COINS',
    brandColor: '#F7931A',
    isOfficialVector: true,
    searchKeywords: ['bitcoin', 'btc', 'satoshi', 'crypto', 'coin'],
    keywords: ['bitcoin', 'btc', 'satoshi', 'crypto', 'coin'],
    officialSVG: btcSvg,
    logoUrl: btcSvg
  },
  {
    id: 'ethereum',
    brandName: 'ETH (Ethereum)',
    name: 'ETH (Ethereum)',
    category: 'CRYPTO COINS',
    brandColor: '#627EEA',
    isOfficialVector: true,
    searchKeywords: ['ethereum', 'eth', 'smart contract', 'crypto', 'vitalik'],
    keywords: ['ethereum', 'eth', 'smart contract', 'crypto', 'vitalik'],
    officialSVG: ethSvg,
    logoUrl: ethSvg
  },
  {
    id: 'litecoin',
    brandName: 'LTC (Litecoin)',
    name: 'LTC (Litecoin)',
    category: 'CRYPTO COINS',
    brandColor: '#345D9D',
    isOfficialVector: true,
    searchKeywords: ['litecoin', 'ltc', 'crypto'],
    keywords: ['litecoin', 'ltc', 'crypto'],
    officialSVG: ltcSvg,
    logoUrl: ltcSvg
  },
  {
    id: 'usdc',
    brandName: 'USD Coin (USDC)',
    name: 'USD Coin (USDC)',
    category: 'CRYPTO COINS',
    brandColor: '#2775CA',
    isOfficialVector: true,
    searchKeywords: ['usdc', 'usd coin', 'circle', 'stablecoin'],
    keywords: ['usdc', 'usd coin', 'circle', 'stablecoin'],
    officialSVG: usdcSvg,
    logoUrl: usdcSvg
  },
  {
    id: 'solana',
    brandName: 'Solana (SOL)',
    name: 'Solana (SOL)',
    category: 'CRYPTO COINS',
    brandColor: '#DC1FFF',
    isOfficialVector: true,
    searchKeywords: ['solana', 'sol', 'crypto'],
    keywords: ['solana', 'sol', 'crypto'],
    officialSVG: solanaSvg,
    logoUrl: solanaSvg
  },

  // --- MOBILE BANKING ---
  // --- DIGITAL PAYMENTS ---
  {
    id: 'cashapp',
    brandName: 'Cash App',
    name: 'Cash App',
    category: 'DIGITAL PAYMENTS',
    brandColor: '#00D632',
    isOfficialVector: true,
    searchKeywords: ['cash app', 'cashapp', 'p2p', 'mobile'],
    keywords: ['cash app', 'cashapp', 'p2p', 'mobile'],
    officialSVG: cashappSvg,
    logoUrl: cashappSvg
  },
  {
    id: 'venmo',
    brandName: 'Venmo',
    name: 'Venmo',
    category: 'DIGITAL PAYMENTS',
    brandColor: '#008CFF',
    isOfficialVector: true,
    searchKeywords: ['venmo', 'p2p', 'mobile', 'paypal'],
    keywords: ['venmo', 'p2p', 'mobile', 'paypal'],
    officialSVG: venmoSvg,
    logoUrl: venmoSvg
  },
  {
    id: 'zelle',
    brandName: 'Zelle',
    name: 'Zelle',
    category: 'DIGITAL PAYMENTS',
    brandColor: '#7414CA',
    isOfficialVector: true,
    searchKeywords: ['zelle', 'bank transfer', 'p2p', 'instant'],
    keywords: ['zelle', 'bank transfer', 'p2p', 'instant'],
    officialSVG: zelleSvg,
    logoUrl: zelleSvg
  },
  {
    id: 'applepay',
    brandName: 'Apple Pay',
    name: 'Apple Pay',
    category: 'DIGITAL PAYMENTS',
    brandColor: '#000000',
    isOfficialVector: true,
    searchKeywords: ['apple pay', 'apple', 'wallet', 'contactless'],
    keywords: ['apple pay', 'apple', 'wallet', 'contactless'],
    officialSVG: applePaySvg,
    logoUrl: applePaySvg
  },
  {
    id: 'googlepay',
    brandName: 'Google Pay',
    name: 'Google Pay',
    category: 'DIGITAL PAYMENTS',
    brandColor: '#1A73E8',
    isOfficialVector: true,
    searchKeywords: ['google pay', 'gpay', 'wallet'],
    keywords: ['google pay', 'gpay', 'wallet'],
    officialSVG: googlePaySvg,
    logoUrl: googlePaySvg
  },
  {
    id: 'chime',
    brandName: 'Chime',
    name: 'Chime',
    category: 'DIGITAL PAYMENTS',
    brandColor: '#25C974',
    isOfficialVector: true,
    searchKeywords: ['chime', 'bank', 'mobile'],
    keywords: ['chime', 'bank', 'mobile'],
    officialSVG: chimeSvg,
    logoUrl: chimeSvg
  },

  // --- BANKING ---
  {
    id: 'bank_transfer',
    brandName: 'Bank Transfer',
    name: 'Bank Transfer',
    category: 'BANKING',
    brandColor: '#1E293B',
    isOfficialVector: true,
    searchKeywords: ['bank', 'transfer', 'wire', 'direct', 'account'],
    keywords: ['bank', 'transfer', 'wire', 'direct', 'account'],
    officialSVG: bankTransferSvg,
    logoUrl: bankTransferSvg
  },
  {
    id: 'swift',
    brandName: 'SWIFT Wire',
    name: 'SWIFT Wire',
    category: 'BANKING',
    brandColor: '#0A2540',
    isOfficialVector: true,
    searchKeywords: ['swift', 'wire', 'international', 'bank'],
    keywords: ['swift', 'wire', 'international', 'bank'],
    officialSVG: swiftSvg,
    logoUrl: swiftSvg
  },
  {
    id: 'ach',
    brandName: 'ACH Direct Deposit',
    name: 'ACH Direct Deposit',
    category: 'BANKING',
    brandColor: '#0052FF',
    isOfficialVector: true,
    searchKeywords: ['ach', 'direct deposit', 'bank', 'us'],
    keywords: ['ach', 'direct deposit', 'bank', 'us'],
    officialSVG: achSvg,
    logoUrl: achSvg
  }
];

/**
 * Global Logo Sync Lookup Utility
 * Returns verified brand logo item matching brandName, id, or query
 */
export function getVerifiedBrandLogo(query?: string): BrandLogoItem | null {
  if (!query) return null;
  const q = query.trim().toLowerCase();
  if (!q) return null;

  return (
    BRAND_LOGOS.find(
      (b) =>
        b.id.toLowerCase() === q ||
        b.brandName.toLowerCase() === q ||
        b.name.toLowerCase() === q ||
        b.searchKeywords.some((k) => k.toLowerCase() === q) ||
        q.includes(b.id.toLowerCase()) ||
        q.includes(b.brandName.toLowerCase())
    ) || null
  );
}

/**
 * Global Logo Sync SVG URL Resolver
 */
export function getVerifiedSvgUrl(query?: string, fallbackUrl?: string): string {
  if (fallbackUrl && (fallbackUrl.startsWith('data:') || fallbackUrl.startsWith('http') || fallbackUrl.startsWith('/'))) {
    // If fallback is already a valid custom SVG data URI, check if a global verified logo exists for query
    const verified = getVerifiedBrandLogo(query);
    if (verified) return verified.officialSVG;
    return fallbackUrl;
  }
  const verified = getVerifiedBrandLogo(query);
  return verified ? verified.officialSVG : (fallbackUrl || '');
}
